import React from 'react';
import { Box, Modal, IconButton, Typography } from '@mui/material';
import { Close, ChevronLeft, ChevronRight } from '@mui/icons-material';
import { DESIGN_TOKENS } from '../../../styles/constants';

interface ImageModalProps {
  open: boolean;
  onClose: () => void;
  images: string[];
  currentIndex: number;
  onNext: () => void;
  onPrevious: () => void;
}

const ImageModal: React.FC<ImageModalProps> = ({
  open,
  onClose,
  images,
  currentIndex,
  onNext,
  onPrevious,
}) => {
  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
      }}
    >
      <Box
        sx={{
          width: '600px',
          backgroundColor: '#FFFFFF',
          borderRadius: '12px',
          boxShadow: '0px 80px 250px 0px rgba(26, 109, 109, 0.1)',
          outline: 'none',
          position: 'relative',
        }}
      >
        {/* Close Button */}
        <Box
          sx={{
            position: 'absolute',
            top: '24px',
            right: '24px',
            zIndex: 10,
          }}
        >
          <IconButton
            onClick={onClose}
            sx={{
              width: '40px',
              height: '40px',
              backgroundColor: '#F6F8F8',
              borderRadius: '40px',
              '&:hover': {
                backgroundColor: '#E5E8E8',
              },
            }}
          >
            <Close sx={{ fontSize: 24, color: '#181818' }} />
          </IconButton>
        </Box>

        {/* Image Container */}
        <Box
          sx={{
            padding: '24px',
            display: 'flex',
            flexDirection: 'column',
            gap: '24px',
          }}
        >
          <Box
            sx={{
              width: '100%',
              height: '552px',
              position: 'relative',
              borderRadius: '16px',
              overflow: 'hidden',
            }}
          >
            <Box
              component="img"
              src={images[currentIndex]}
              alt={`Review photo ${currentIndex + 1}`}
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
              }}
            />
          </Box>

          {/* Navigation */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '32px',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                padding: '8px',
                backgroundColor: '#F6F8F8',
                borderRadius: '30px',
              }}
            >
              {images.map((_, index) => (
                <Box
                  key={index}
                  sx={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: index === currentIndex ? '#181818' : '#C5CBD3',
                  }}
                />
              ))}
            </Box>
            
            <Typography
              sx={{
                fontFamily: DESIGN_TOKENS.typography.fontFamily,
                fontSize: '13px',
                fontWeight: 400,
                lineHeight: '18.2px',
                letterSpacing: '-0.08px',
                color: '#93989C',
              }}
            >
              {currentIndex + 1} of {images.length}
            </Typography>
          </Box>

          {/* Previous Button */}
          {currentIndex > 0 && (
            <IconButton
              onClick={onPrevious}
              sx={{
                position: 'absolute',
                left: '4px',
                top: '50%',
                transform: 'translateY(-50%)',
                width: '40px',
                height: '40px',
                backgroundColor: '#FFFFFF',
                borderRadius: '40px',
                '&:hover': {
                  backgroundColor: '#F6F8F8',
                },
              }}
            >
              <ChevronLeft sx={{ fontSize: 24, color: '#181818' }} />
            </IconButton>
          )}

          {/* Next Button */}
          {currentIndex < images.length - 1 && (
            <IconButton
              onClick={onNext}
              sx={{
                position: 'absolute',
                right: '4px',
                top: '50%',
                transform: 'translateY(-50%)',
                width: '40px',
                height: '40px',
                backgroundColor: '#FFFFFF',
                borderRadius: '40px',
                '&:hover': {
                  backgroundColor: '#F6F8F8',
                },
              }}
            >
              <ChevronRight sx={{ fontSize: 24, color: '#181818' }} />
            </IconButton>
          )}
        </Box>
      </Box>
    </Modal>
  );
};

export default ImageModal;


